import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import {
	addProjectToCompare,
	removeProjectFromCompare,
} from "../api/supplier-search-api";
import { setAddProjectIndex } from "actions/searchActions";
import * as constants from "actions/actionTypes";
import { showToast } from "utils/helpers";
import type {
	AddProjectToCompareParams,
	RemoveProjectFromCompareParams,
} from "../types/supplier-search.types";

interface SwapProjectParams extends AddProjectToCompareParams {
	project: Record<string, unknown>;
}

interface MutateWithRefreshCallbacks {
	onSuccess?: () => void;
}

export const useAddProjectToCompare = () => {
	const dispatch = useDispatch();

	const mutation = useMutation<unknown, Error, AddProjectToCompareParams>({
		mutationFn: addProjectToCompare,
		onError: () => {
			showToast("Unable to add project", "Error", false);
		},
	});

	const mutateWithRefresh = (
		params: SwapProjectParams,
		currentAddProjectIndexes: number[],
		isDirectCompare: boolean,
		callbacks?: MutateWithRefreshCallbacks
	) => {
		mutation.mutate(params, {
			onSuccess: () => {
				// Consume the slot
				dispatch(setAddProjectIndex(currentAddProjectIndexes.slice(1)));

				// Targeted slot update — only replaces the one project, leaves everything else intact
				dispatch({
					type: constants.SWAP_COMPARE_PROJECT_SLOT,
					payload: {
						companyId: params.companyId,
						slot: params.slot,
						project: params.project,
						isDirectCompare,
					},
				});

				showToast(
					"Project successfully added to compare view",
					"Success",
					true
				);
				callbacks?.onSuccess?.();
			},
		});
	};

	return { ...mutation, mutateWithRefresh };
};

export const useRemoveProjectFromCompare = () => {
	const dispatch = useDispatch();

	const mutation = useMutation<unknown, Error, RemoveProjectFromCompareParams>({
		mutationFn: removeProjectFromCompare,
		onError: () => {
			showToast("Unable to remove project", "Error", false);
		},
	});

	const mutateWithRefresh = (
		params: RemoveProjectFromCompareParams,
		isDirectCompare: boolean,
		callbacks?: MutateWithRefreshCallbacks
	) => {
		// Optimistic local update — set the slot to null immediately
		dispatch({
			type: constants.SWAP_COMPARE_PROJECT_SLOT,
			payload: {
				companyId: params.companyId,
				slot: params.slot + 1, // API uses 0-based, reducer uses 1-based
				project: null,
				isDirectCompare,
			},
		});

		mutation.mutate(params, {
			onSuccess: () => {
				showToast(
					"Project removed from compare view",
					"Success",
					true
				);
				callbacks?.onSuccess?.();
			},
		});
	};

	return { ...mutation, mutateWithRefresh };
};
